import type {
  CaptionAnimationStyle,
  ClipMetadata,
  Subtitle,
  SubtitleWord,
} from "../types/timeline";
import type { TextStyle } from "./types";

/**
 * Named caption looks.
 *
 * A preset bundles the three things that make captions feel like captions
 * (type, motion, and how much text is on screen at once) so a user picks one
 * name instead of assembling ten knobs.
 *
 * Every `fontFamily` here must be self-hosted in
 * `apps/web/public/fonts/google-fonts.css` AND present in the desktop build's
 * curated font set (`apps/web/vite-plugins/prune-fonts.ts`), otherwise the
 * preset silently falls back to a system face in the packaged app.
 * `caption-presets.test.ts` enforces the first half of that.
 */
export interface CaptionPreset {
  readonly id: CaptionPresetId;
  /** Shown in the style picker. */
  readonly name: string;
  /** One line explaining when to reach for it. */
  readonly description: string;
  readonly style: TextStyle;
  readonly animationStyle: CaptionAnimationStyle;
  /** Colour of the word currently being spoken. Ignored when static. */
  readonly highlightColor?: string;
  /**
   * Colour behind the active word.
   *
   * Nothing reads this yet: `renderCaptionFrame` paints one plate per line from
   * `style.backgroundColor` and has no per-word plate. A preset that wants a
   * chip behind the spoken word gets it by pairing `backgroundColor` with the
   * `word-by-word` animation, which puts a single word on the line, so the line
   * plate *is* the chip. "Marker" below is that. Setting this field alone
   * renders nothing.
   */
  readonly highlightBackground?: string;
  /** Hard cap on words per caption: the lever that makes "Stacked" stack. */
  readonly maxWordsPerCaption: number;
  /** Wrap width as a fraction of frame width. */
  readonly maxWidthRatio: number;
  /** Vertical placement as a fraction of frame height (0 = top, 1 = bottom). */
  readonly verticalAnchor: number;
  /** True when the preset needs per-word timings to look right. */
  readonly requiresWordTimings: boolean;
}

export type CaptionPresetId =
  | "stacked"
  | "minimal"
  | "serif"
  | "punch"
  | "karaoke-pop"
  | "neon"
  | "typewriter"
  | "soft"
  | "spotlight"
  | "outline"
  | "block"
  | "marker";

/** Shared defaults so each preset only states what makes it distinct. */
const base = {
  fontStyle: "normal",
  textAlign: "center",
  verticalAlign: "middle",
  lineHeight: 1.15,
  letterSpacing: 0,
} as const;

export const CAPTION_PRESETS: readonly CaptionPreset[] = [
  {
    id: "stacked",
    name: "Stacked",
    description: "Big condensed words, one or two per line. The TikTok default.",
    style: {
      ...base,
      fontFamily: "Anton",
      fontSize: 84,
      fontWeight: 400,
      color: "#ffffff",
      strokeColor: "#000000",
      strokeWidth: 8,
      shadowColor: "rgba(0, 0, 0, 0.55)",
      shadowBlur: 12,
      shadowOffsetY: 4,
      letterSpacing: 1,
    },
    animationStyle: "word-highlight",
    highlightColor: "#ffe14d",
    maxWordsPerCaption: 3,
    maxWidthRatio: 0.8,
    verticalAnchor: 0.72,
    requiresWordTimings: true,
  },
  {
    id: "minimal",
    name: "Minimal",
    description: "Quiet single-line subtitles that stay out of the way.",
    style: {
      ...base,
      fontFamily: "Inter",
      fontSize: 44,
      fontWeight: 500,
      color: "#ffffff",
      shadowColor: "rgba(0, 0, 0, 0.7)",
      shadowBlur: 8,
      shadowOffsetY: 2,
    },
    animationStyle: "none",
    maxWordsPerCaption: 8,
    maxWidthRatio: 0.85,
    verticalAnchor: 0.86,
    requiresWordTimings: false,
  },
  {
    id: "serif",
    name: "Serif",
    description: "Editorial and cinematic, for documentary or brand films.",
    style: {
      ...base,
      fontFamily: "DM Serif Display",
      fontSize: 52,
      fontWeight: 400,
      color: "#f7f3ea",
      shadowColor: "rgba(0, 0, 0, 0.6)",
      shadowBlur: 14,
      shadowOffsetY: 3,
      letterSpacing: 0.5,
    },
    animationStyle: "none",
    maxWordsPerCaption: 9,
    maxWidthRatio: 0.78,
    verticalAnchor: 0.84,
    requiresWordTimings: false,
  },
  {
    id: "punch",
    name: "Punch",
    description: "Heavy type that pops on the beat. Built for hooks.",
    style: {
      ...base,
      fontFamily: "Archivo Black",
      fontSize: 78,
      fontWeight: 400,
      color: "#ffffff",
      strokeColor: "#000000",
      strokeWidth: 10,
      shadowColor: "rgba(0, 0, 0, 0.5)",
      shadowBlur: 10,
      shadowOffsetY: 6,
    },
    animationStyle: "bounce",
    highlightColor: "#ff3d7f",
    maxWordsPerCaption: 4,
    maxWidthRatio: 0.82,
    verticalAnchor: 0.7,
    requiresWordTimings: true,
  },
  {
    id: "karaoke-pop",
    name: "Karaoke Pop",
    description: "Colour sweeps across each word as it is spoken.",
    style: {
      ...base,
      fontFamily: "Montserrat",
      fontSize: 64,
      fontWeight: 800,
      color: "#ffffff",
      strokeColor: "#12042b",
      strokeWidth: 6,
      shadowColor: "rgba(18, 4, 43, 0.5)",
      shadowBlur: 10,
      shadowOffsetY: 4,
    },
    animationStyle: "karaoke",
    highlightColor: "#43e6ff",
    maxWordsPerCaption: 6,
    maxWidthRatio: 0.82,
    verticalAnchor: 0.75,
    requiresWordTimings: true,
  },
  {
    id: "neon",
    name: "Neon",
    description: "Glowing condensed caps for night-time and gaming clips.",
    style: {
      ...base,
      fontFamily: "Bebas Neue",
      fontSize: 76,
      fontWeight: 400,
      color: "#f2fbff",
      shadowColor: "#12d8ff",
      shadowBlur: 26,
      shadowOffsetY: 0,
      letterSpacing: 2,
    },
    animationStyle: "word-highlight",
    highlightColor: "#12d8ff",
    maxWordsPerCaption: 4,
    maxWidthRatio: 0.8,
    verticalAnchor: 0.74,
    requiresWordTimings: true,
  },
  {
    id: "typewriter",
    name: "Typewriter",
    description: "Monospace text that types itself out, one character at a time.",
    style: {
      ...base,
      fontFamily: "Space Mono",
      fontSize: 40,
      fontWeight: 700,
      color: "#ffffff",
      backgroundColor: "rgba(0, 0, 0, 0.65)",
      textAlign: "left",
      letterSpacing: 0.5,
    },
    animationStyle: "typewriter",
    maxWordsPerCaption: 10,
    maxWidthRatio: 0.76,
    verticalAnchor: 0.82,
    requiresWordTimings: false,
  },
  {
    id: "soft",
    name: "Soft",
    description: "Rounded type on a gentle pill. Friendly, vlog-ready.",
    style: {
      ...base,
      fontFamily: "Poppins",
      fontSize: 50,
      fontWeight: 600,
      color: "#1b1b1f",
      backgroundColor: "rgba(255, 255, 255, 0.92)",
      shadowColor: "rgba(0, 0, 0, 0.18)",
      shadowBlur: 16,
      shadowOffsetY: 4,
    },
    animationStyle: "word-by-word",
    highlightColor: "#6c4cff",
    maxWordsPerCaption: 6,
    maxWidthRatio: 0.8,
    verticalAnchor: 0.83,
    requiresWordTimings: true,
  },
  {
    id: "spotlight",
    name: "Spotlight",
    description: "The whole line stays up and the word being said lights up.",
    style: {
      ...base,
      fontFamily: "Outfit",
      fontSize: 60,
      fontWeight: 700,
      color: "#ffffff",
      strokeColor: "#0b0b12",
      strokeWidth: 5,
      shadowColor: "rgba(0, 0, 0, 0.5)",
      shadowBlur: 10,
      shadowOffsetY: 3,
    },
    animationStyle: "word-highlight",
    highlightColor: "#4dffa6",
    maxWordsPerCaption: 5,
    maxWidthRatio: 0.84,
    verticalAnchor: 0.76,
    requiresWordTimings: true,
  },
  {
    id: "outline",
    name: "Outline",
    description: "White type in a heavy black outline. Readable over anything.",
    style: {
      ...base,
      fontFamily: "Archivo Black",
      fontSize: 72,
      fontWeight: 400,
      color: "#ffffff",
      strokeColor: "#000000",
      // The renderer strokes and then fills over the stroke, so roughly half of
      // this sits inside the glyph: 18 reads as a 9px rim at 1080x1920.
      strokeWidth: 18,
      letterSpacing: 0.5,
    },
    animationStyle: "none",
    maxWordsPerCaption: 5,
    maxWidthRatio: 0.84,
    verticalAnchor: 0.8,
    requiresWordTimings: false,
  },
  {
    id: "block",
    name: "Block",
    description: "A solid bar behind each line, for footage too busy to read over.",
    style: {
      ...base,
      fontFamily: "Inter",
      fontSize: 52,
      fontWeight: 700,
      color: "#ffffff",
      // Opaque on purpose. The point of this one is that nothing in the shot
      // shows through, so a tint would defeat it.
      backgroundColor: "#000000",
    },
    animationStyle: "none",
    maxWordsPerCaption: 7,
    maxWidthRatio: 0.8,
    verticalAnchor: 0.85,
    requiresWordTimings: false,
  },
  {
    id: "marker",
    name: "Marker",
    description: "One word at a time on a solid highlighter block.",
    style: {
      ...base,
      fontFamily: "Montserrat",
      fontSize: 80,
      fontWeight: 800,
      color: "#12121a",
      backgroundColor: "#ffe14d",
      letterSpacing: 0.5,
    },
    // Paired with the plate above, this is what makes the chip: word-by-word
    // puts exactly one word on the line, so the line's plate is a block behind
    // the spoken word. No highlight colour, because recolouring the only word
    // on screen would leave nothing to contrast it against.
    animationStyle: "word-by-word",
    maxWordsPerCaption: 4,
    maxWidthRatio: 0.8,
    verticalAnchor: 0.72,
    requiresWordTimings: true,
  },
] as const;

export const DEFAULT_CAPTION_PRESET_ID: CaptionPresetId = "stacked";

export function getCaptionPreset(id: string): CaptionPreset | undefined {
  return CAPTION_PRESETS.find((preset) => preset.id === id);
}

/**
 * Looks up a preset, falling back to the default rather than throwing: a
 * project saved with a preset we later rename should still open.
 */
export function resolveCaptionPreset(id: string | undefined): CaptionPreset {
  return (
    (id ? getCaptionPreset(id) : undefined) ??
    getCaptionPreset(DEFAULT_CAPTION_PRESET_ID)!
  );
}

/**
 * Adapts a preset to the frame it is being drawn into, so a look designed
 * against 1080p vertical holds up on a 4K or square canvas.
 */
export function scaleCaptionStyle(
  preset: CaptionPreset,
  frameHeight: number,
): TextStyle {
  const REFERENCE_HEIGHT = 1920;
  const scale = frameHeight / REFERENCE_HEIGHT;

  return {
    ...preset.style,
    fontSize: Math.round(preset.style.fontSize * scale),
    strokeWidth: preset.style.strokeWidth
      ? Math.max(1, preset.style.strokeWidth * scale)
      : preset.style.strokeWidth,
    shadowBlur: preset.style.shadowBlur
      ? preset.style.shadowBlur * scale
      : preset.style.shadowBlur,
    shadowOffsetY: preset.style.shadowOffsetY
      ? preset.style.shadowOffsetY * scale
      : preset.style.shadowOffsetY,
    letterSpacing: preset.style.letterSpacing * scale,
  };
}

/**
 * A preset that needs word timings cannot animate without them. Rather than
 * play fake, drifting karaoke off interpolated timings, drop to the static
 * look and let the caller tell the user why.
 */
export function effectiveAnimationStyle(
  preset: CaptionPreset,
  hasWordTimings: boolean,
): CaptionAnimationStyle {
  if (preset.requiresWordTimings && !hasWordTimings) {
    return "none";
  }
  return preset.animationStyle;
}

export interface CaptionClipSpec {
  readonly style: TextStyle;
  readonly metadata: ClipMetadata;
  /** Normalized 0..1 frame position for the caption's centre. */
  readonly position: { readonly x: number; readonly y: number };
}

/**
 * Turns a transcript cue plus a preset into everything needed to create the
 * caption's text clip.
 *
 * Word timings are rebased to be **clip-relative** here: the clip starts at
 * `subtitle.startTime`, so a word two seconds into the cue is stored as 2, not
 * as its absolute timeline position. That is what lets a caption be dragged or
 * trimmed later without its karaoke drifting out of sync.
 */
export function buildCaptionClipSpec(
  subtitle: Subtitle,
  preset: CaptionPreset,
  frameHeight: number,
): CaptionClipSpec {
  // Rounded to the microsecond: subtracting the clip start leaves float noise
  // (11.2 - 10 = 1.1999999999999993) that would otherwise be serialized into
  // every saved project.
  const rebase = (t: number) => Math.round((t - subtitle.startTime) * 1e6) / 1e6;

  const words: SubtitleWord[] = (subtitle.words ?? []).map((word) => ({
    text: word.text,
    startTime: rebase(word.startTime),
    endTime: rebase(word.endTime),
  }));

  const animationStyle = effectiveAnimationStyle(preset, words.length > 0);

  return {
    style: scaleCaptionStyle(preset, frameHeight),
    position: { x: 0.5, y: preset.verticalAnchor },
    metadata: {
      captionPresetId: preset.id,
      captionWords: words.length > 0 ? words : undefined,
      captionAnimationStyle: animationStyle,
      captionHighlightColor: preset.highlightColor,
      captionMaxWidthRatio: preset.maxWidthRatio,
    },
  };
}

/**
 * Regroups transcript words into cues sized for a preset.
 *
 * The same transcript yields three-word cues for Stacked and eight-word cues
 * for Minimal, so switching preset has to re-cut the text, not just restyle it.
 */
export function groupWordsForPreset(
  words: readonly SubtitleWord[],
  preset: CaptionPreset,
  makeId: (index: number) => string,
): Subtitle[] {
  if (words.length === 0) return [];

  const cues: Subtitle[] = [];
  let current: SubtitleWord[] = [];

  const flush = () => {
    if (current.length === 0) return;
    const first = current[0];
    const last = current[current.length - 1];
    cues.push({
      id: makeId(cues.length),
      text: current.map((w) => w.text.trim()).join(" "),
      startTime: first.startTime,
      endTime: last.endTime,
      words: current,
      animationStyle: preset.animationStyle,
    });
    current = [];
  };

  for (const word of words) {
    current.push(word);

    // Break on sentence endings too, so a cue does not straddle a full stop
    // just because the word budget had room left.
    const endsSentence = /[.!?]$/.test(word.text.trim());
    if (current.length >= preset.maxWordsPerCaption || (endsSentence && current.length >= 2)) {
      flush();
    }
  }

  flush();
  return cues;
}
